import { useState, useEffect } from 'react';
import { DeviceMobile, X, ShareNetwork } from '@phosphor-icons/react';

const InstallPWAButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Detectar si ya está instalada (standalone)
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    if (standalone) return;

    // Detectar iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // En iOS, siempre es "instalable" (manualmente)
    if (isIOSDevice) {
      setIsInstallable(true);
      return;
    }

    // Verificar si fue dismissed en esta sesión
    if (sessionStorage.getItem('pwa-dismissed')) {
      setIsDismissed(true);
      return;
    }

    // Escuchar el evento beforeinstallprompt (Chrome, Edge, Samsung)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Escuchar si la app fue instalada
    window.addEventListener('appinstalled', () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstallable(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setShowIOSInstructions(false);
    sessionStorage.setItem('pwa-dismissed', 'true');
  };

  // No mostrar si ya está instalada, no es instalable, o fue dismissed
  if (isStandalone || !isInstallable || isDismissed) {
    return null;
  }

  return (
    <>
      {/* Botón lateral de instalar */}
      <div className="fixed right-0 bottom-24 z-50 animate-slide-in-right">
        <button
          onClick={handleInstallClick}
          className="bg-brand-primary text-white pl-3 pr-2 py-2 rounded-l-xl shadow-lg flex items-center gap-2 hover:bg-brand-primary-hover transition-colors"
        >
          {isIOS ? <ShareNetwork size={16} /> : <DeviceMobile size={16} />}
          <span className="text-xs font-medium whitespace-nowrap">
            {isIOS ? 'Agregar a inicio' : 'Instala la app'}
          </span>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            className="ml-1 p-0.5 rounded-full hover:bg-brand-primary transition-colors"
          >
            <X size={12} />
          </button>
        </button>
      </div>

      {/* Modal de instrucciones para iOS */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowIOSInstructions(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-t-3xl w-full max-w-lg p-6 pb-8 animate-slide-up">
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Instalar Devolada en tu iPhone
            </h3>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-alert-soft-bg-strong text-brand-primary flex items-center justify-center font-semibold text-sm">
                  1
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    Toca el botón <strong>Compartir</strong> <ShareNetwork size={14} className="inline text-blue-500" /> en la barra de Safari
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-alert-soft-bg-strong text-brand-primary flex items-center justify-center font-semibold text-sm">
                  2
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    Desplázate y selecciona <strong>"Agregar a pantalla de inicio"</strong>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-alert-soft-bg-strong text-brand-primary flex items-center justify-center font-semibold text-sm">
                  3
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    Toca <strong>"Agregar"</strong> para instalar la app
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6 py-3 bg-brand-primary text-white rounded-xl font-medium"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;
